import { FC, useEffect, useState } from 'react';
import { Control } from 'react-hook-form';

import { ConfirmationDialog } from '@/components/dialogs/generic/confirmation';
import { OpstartSolverResultaat } from '@/components/dialogs/solver/OpstartSolverResultaat';
import useOpstartSolver, { OpstartSolverInputs } from '@/components/dialogs/solver/useOpstartSolver';
import MwdDialog from '@/components/UI/Dialog/Dialog';
import { CreatableSelectField } from '@/components/UI/Fields/ReactFormFields/CreatableSelectField';
import { MaterieelTypenSearchField } from '@/components/UI/Fields/ReactFormFields/MaterieelTypenSearchField';
import { SelectField } from '@/components/UI/Fields/ReactFormFields/SelectField';
import { LoaderWithOverlay } from '@/components/UI/Loader/LoaderWithOverlay';
import { useLoading } from '@/contextProviders/loadingProvider';
import { useMaterieelTypenSearch } from '@/hooks/useMaterieelTypenSearch';
import useDienstregelpunten from '@/state/selectors/useDienstregelpunten';

import styles from '@/components/UI/Dialog/Dialog.module.css';
import inputStyles from '@/components/UI/Fields/Fields.module.css';

export type OpstartSolverDialogProps = {
  showDialog: boolean;
  handleOpenChange: (p: boolean) => void;
};

const OpstartSolverDialog: FC<OpstartSolverDialogProps> = ({ showDialog, handleOpenChange }) => {
  const { handleSubmit, onSubmit, control, resetDialog, setValue } = useOpstartSolver(handleOpenChange);
  const { loading, setLoading } = useLoading();
  const dienstregelpuntenOptions = useDienstregelpunten();
  const { handleMaterieelTypenChange, selectedMaterieelTypen } = useMaterieelTypenSearch();
  useEffect(() => {
    if (selectedMaterieelTypen.length > 0) {
      setValue('materieelTypes', selectedMaterieelTypen);
    }
  }, [selectedMaterieelTypen, setValue]);
  const [pendingData, setPendingData] = useState<OpstartSolverInputs | null>(null);
  const [result, setResult] = useState<OpstartSolverResultaat | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showResultDialog, setShowResultDialog] = useState(false);

  const createOption = (label: string) => ({
    label,
    value: label,
  });

  const handleFormSubmit = (data: any) => {
    setPendingData(data);
    setShowConfirmDialog(true);
  };

  const performOpstartSolver = async () => {
    if (pendingData) {
      const result = await onSubmit(pendingData);
      setShowConfirmDialog(false);
      setPendingData(null);
      if (result) {
        setResult(result);
        setShowResultDialog(true);
      }
    }
  };

  const handleResultDialogClose = () => {
    setShowResultDialog(false);
    handleOpenChange(false);
  };

  const verwerkResultaat = (resultaat: OpstartSolverResultaat) => {
    const voorEnNaEnVerschil = (voor: number, na: number) => {
      return `voor: ${voor}, na: ${na}, verschil: ${Math.abs(voor - na)}`;
    };
    return [
      `Aantal manco werklijnen: ${voorEnNaEnVerschil(resultaat.aantalMancoWerklijnenVoorMutatie, resultaat.aantalMancoWerklijnenNaMutatie)}`,
      `Aantal conflictvrije werklijnen met inzetten op opstartdatum: ${voorEnNaEnVerschil(resultaat.aantalConflictVrijeWerklijnenMetInzettenOpOpstartDatumVoorMutatie, resultaat.aantalConflictVrijeWerklijnenMetInzettenOpOpstartDatumNaMutatie)}`,
      `Aantal werklijnen met plaatsconflict vóór mutatie: ${resultaat.aantalNietMancoWerklijnenMetPlaatsconflictVoorMutatie}`,
    ];
  };

  const getMogelijkeDagen = (): { label: string; value: string }[] => {
    const vandaag = new Date();
    const morgen = new Date();
    const overmorgen = new Date();

    morgen.setDate(vandaag.getDate() + 1);
    overmorgen.setDate(vandaag.getDate() + 2);

    const format = (date: Date) => date.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' });

    return [
      { label: `${format(morgen)} (morgen)`, value: morgen.toISOString().split('T')[0] },
      { label: `${format(overmorgen)} (overmorgen)`, value: overmorgen.toISOString().split('T')[0] },
    ];
  };

  return (
    <>
      {loading && (
        <LoaderWithOverlay isLoading={loading} handleOpenChange={setLoading} overlayOpacity='nonTransparant' />
      )}
      <MwdDialog
        title='OpstartSolver'
        className={styles.opstartSolverDialogStyle}
        showDialog={showDialog}
        handleOpenChange={handleOpenChange}
        scrollEnabled={true}
        hasCloseInHeader={true}
      >
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <div style={{ display: 'flex', gap: 4, flexDirection: 'column' }}>
            <span>
              De OpstartSolver selecteert alle werklijnen die voldoen aan je selectiecriteria en maakt deze manco.
              Vervolgens wordt per dienstregelpunt gekeken welke eenheden beschikbaar zijn en worden de eenheden volgens
              het last-in-first-out-principe gekoppeld aan werklijnen. Na het uitvoeren worden eventuele
              plaatsconflicten dus manco bij tekort aan materieel. Geraakte werklijnen kunnen tijdens de verwerking niet
              bijgestuurd worden.
            </span>
            <div>
              <CreatableSelectField
                className={`${inputStyles.inputField} ${styles.opstartSolverInputStyle}`}
                label='Dienstregelpunten'
                control={control as Control<any>}
                testId='dienstregelpunten'
                createLabel='Dienstregelpunt:'
                isMulti={true}
                isClearable
                isSearchable
                openMenuOnClick={false}
                name='dienstregelpunten'
                matchFromStart={true}
                defaultValue=''
                options={dienstregelpuntenOptions ? dienstregelpuntenOptions.map((item) => createOption(item)) : []}
                placeholder='Voeg dienstregelpunt(en) toe'
              />
              <div style={{ fontSize: 'small', color: 'var(--tractie-color-content-subtle)' }}>
                Alleen toepassen op geselecteerde dienstregelpunten. Laat leeg om ALLE dienstregelpunten te gebruiken.
              </div>
            </div>
            <div>
              <MaterieelTypenSearchField
                className={`${inputStyles.inputField} ${styles.opstartSolverInputStyle}`}
                name='materieelTypes'
                marginBottom={2}
                control={control as Control<any>}
                defaultValue={selectedMaterieelTypen.map((item) => createOption(item))}
                onChangeCallback={handleMaterieelTypenChange}
                required={true}
              />
              <div style={{ fontSize: 'small', color: 'var(--tractie-color-content-subtle)' }}>
                Alleen toepassen op geselecteerde materieeltypes (verplicht).
              </div>
            </div>
            <div>
              <SelectField
                className={`${inputStyles.inputField} ${styles.opstartSolverInputStyle}`}
                testId='opstartDatum'
                control={control}
                name='opstartDatum'
                label='Verkeersdag'
                placeholder='Selecteer een datum'
                options={getMogelijkeDagen()}
                required={true}
              />
            </div>
          </div>
          <MwdDialog.Footer
            submitCaption='Start OpstartSolver'
            confirmButtonType='destructive'
            cancelCallback={resetDialog}
            justifyContentStyle='space-between'
          />
          {showConfirmDialog && (
            <ConfirmationDialog
              className={styles.opstartSolverDialogConfirmationStyle}
              handleOpenChange={setShowConfirmDialog}
              showDialog={showConfirmDialog}
              onCancel={() => setShowConfirmDialog(false)}
              onConfirm={performOpstartSolver}
              confirmButtonType='destructive'
              cancelText='Annuleren'
              confirmText='Starten'
              title='Bevestigen'
            >
              <span>
                Weet je zeker dat je voor de geselecteerde dienstregelpunten, materieeltypes en datum de OpstartSolver
                wilt gebruiken?
              </span>
              <p>Dit kan niet ongedaan worden gemaakt. Geraakte werklijnen kunnen tijdelijk niet bijgestuurd worden.</p>
            </ConfirmationDialog>
          )}
          {showResultDialog && (
            <ConfirmationDialog
              className={styles.opstartSolverDialogResultStyle}
              showDialog={showResultDialog}
              handleOpenChange={setShowResultDialog}
              onConfirm={handleResultDialogClose}
              confirmButtonType='primary'
              confirmText='Sluiten'
              title='Resultaat'
            >
              <ul>{result && verwerkResultaat(result).map((item) => <li key={item}>{item}</li>)}</ul>
            </ConfirmationDialog>
          )}
        </form>
      </MwdDialog>
    </>
  );
};
export default OpstartSolverDialog;
